<div class="table-selection-form">
  <h2 mat-dialog-title>{{ data.title }}</h2>
  
  <mat-dialog-content>
    <!-- Grid Dimensions Info -->
    <div class="form-section">
      <div class="grid-info">
        <mat-icon>grid_on</mat-icon>
        <span>Selected area: <strong>{{ data.rows }} rows × {{ data.columns }} columns</strong></span>
      </div>
    </div>

    <!-- Header Orientation -->
    <div class="form-section">
      <h4>Header Orientation</h4>
      <mat-radio-group [(ngModel)]="headerOrientation" (change)="onOrientationChange()">
        <mat-radio-button value="horizontal" class="orientation-option">
          <div class="orientation-description">
            <strong>Horizontal Headers</strong>
            <span>Headers in top row, data flows down</span>
          </div>
        </mat-radio-button>
        <mat-radio-button value="vertical" class="orientation-option">
          <div class="orientation-description">
            <strong>Vertical Headers</strong>
            <span>Headers in left column, data flows right</span>
          </div>
        </mat-radio-button>
      </mat-radio-group>
    </div>

    <!-- Dataset Selection for Grouping -->
    <div class="form-section">
      <h4>Group By Dataset & Field</h4>
      <div class="form-row">
        <mat-form-field class="form-field" appearance="outline">
          <mat-label>Select Dataset</mat-label>
          <mat-select [(ngModel)]="selectedDataset" (selectionChange)="onDatasetSelect()">
            <mat-option *ngFor="let dataset of data.availableDatasets" [value]="dataset">
              {{ dataset.datasetName }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field class="form-field" appearance="outline" *ngIf="selectedDataset">
          <mat-label>Select Field</mat-label>
          <mat-select [(ngModel)]="selectedField" (selectionChange)="onFieldSelect()">
            <mat-option *ngFor="let field of datasetFields" [value]="field">
              <span class="field-option">
                <strong>{{ field.fieldName }}</strong>
                <span class="field-type">({{ field.dataType }}{{ field.isPrimaryKey ? ', Primary Key' : '' }})</span>
              </span>
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Custom name for dimension column/row -->
      <div class="form-row" *ngIf="selectedField">
        <mat-form-field class="form-field" appearance="outline">
          <mat-label>{{ headerOrientation === 'horizontal' ? 'Column' : 'Row' }} Name</mat-label>
          <input matInput [(ngModel)]="groupByFieldCustomName" 
                 [placeholder]="selectedField.fieldName">
        </mat-form-field>
      </div>
    </div>

    <!-- Header Configuration -->
    <div class="form-section" *ngIf="selectedDataset && selectedField">
      <h4>Configure {{ headerOrientation === 'horizontal' ? 'Column' : 'Row' }} Headers ({{ getHeaderCount() }} headers)</h4>
      
      <div class="headers-config">
        <div class="header-item" *ngFor="let header of headers; let i = index">
          <div class="header-number">
            {{ headerOrientation === 'horizontal' ? 'Column' : 'Row' }} {{ i + 2 }}:
          </div>
          
          <mat-form-field class="metric-select" appearance="outline">
            <mat-label>Select Metric</mat-label>
            <mat-select 
              [value]="getSelectedMetric(header)"
              (selectionChange)="onMetricSelect(header, $event.value)">
              <mat-option *ngFor="let metric of data.availableMetrics" [value]="metric">
                {{ metric.metricName }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field class="metric-name" appearance="outline" *ngIf="header.metricId">
            <mat-label>Custom Name</mat-label>
            <input matInput [(ngModel)]="header.customName" 
                   [placeholder]="header.metricName || 'Enter custom name'">
          </mat-form-field>
        </div>
      </div>
    </div>

    <!-- Color Configuration -->
    <div class="form-section" *ngIf="selectedDataset && selectedField">
      <h4>Table Colors</h4>
      
      <div class="color-config-grid">
        <!-- Header Colors -->
        <div class="color-group">
          <h5>Headers</h5>
          <div class="color-row">
            <mat-form-field appearance="outline" class="color-field">
              <mat-label>Background</mat-label>
              <input matInput type="color" [(ngModel)]="colorConfig.headerBackgroundColor">
            </mat-form-field>
            <mat-form-field appearance="outline" class="color-field">
              <mat-label>Text</mat-label>
              <input matInput type="color" [(ngModel)]="colorConfig.headerTextColor">
            </mat-form-field>
          </div>
        </div>

        <!-- Grouping Colors -->
        <div class="color-group">
          <h5>{{ headerOrientation === 'horizontal' ? 'First Column' : 'First Row' }} (Groups)</h5>
          <div class="color-row">
            <mat-form-field appearance="outline" class="color-field">
              <mat-label>Background</mat-label>
              <input matInput type="color" [(ngModel)]="colorConfig.groupingBackgroundColor">
            </mat-form-field>
            <mat-form-field appearance="outline" class="color-field">
              <mat-label>Text</mat-label>
              <input matInput type="color" [(ngModel)]="colorConfig.groupingTextColor">
            </mat-form-field>
          </div>
        </div>

        <!-- Data Colors -->
        <div class="color-group">
          <h5>Data Cells</h5>
          <div class="color-row">
            <mat-form-field appearance="outline" class="color-field">
              <mat-label>Background 1</mat-label>
              <input matInput type="color" [(ngModel)]="colorConfig.dataBackgroundColor1">
            </mat-form-field>
            <mat-form-field appearance="outline" class="color-field">
              <mat-label>Background 2</mat-label>
              <input matInput type="color" [(ngModel)]="colorConfig.dataBackgroundColor2">
            </mat-form-field>
            <mat-form-field appearance="outline" class="color-field">
              <mat-label>Text</mat-label>
              <input matInput type="color" [(ngModel)]="colorConfig.dataTextColor">
            </mat-form-field>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview -->
    <div class="form-section" *ngIf="selectedDataset && selectedField">
      <h4>Table Preview</h4>
      <div class="table-preview">
        <div class="preview-description">
          <p *ngIf="headerOrientation === 'horizontal'">
            <strong>First column:</strong> "{{ groupByFieldCustomName || selectedField.fieldName }}" - {{ selectedField.fieldName }} values from {{ selectedDataset.datasetName }}<br>
            <strong>Other columns:</strong> Metric values with custom names
          </p>
          <p *ngIf="headerOrientation === 'vertical'">
            <strong>First row:</strong> "{{ groupByFieldCustomName || selectedField.fieldName }}" - {{ selectedField.fieldName }} values from {{ selectedDataset.datasetName }}<br>
            <strong>Other rows:</strong> Metric values with custom names
          </p>
        </div>
        
        <!-- Table preview -->
        <div class="mini-table-preview">
          <table>
            <thead *ngIf="getPreviewTableStructure().headers.length > 0">
              <tr>
                <th *ngFor="let header of getPreviewTableStructure().headers; let i = index"
                    [ngStyle]="{
                      'background-color': colorConfig.headerBackgroundColor,
                      'color': colorConfig.headerTextColor
                    }">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of getPreviewTableStructure().dataRows; let rowIndex = index">
                <td *ngFor="let cell of row; let cellIndex = index"
                    [ngStyle]="getPreviewCellStyle(rowIndex, cellIndex)">
                  <span>{{ cell }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </mat-dialog-content>

  <mat-dialog-actions class="dialog-actions">
    <button mat-button (click)="onCancel()">Cancel</button>
    <button 
      mat-raised-button 
      color="primary" 
      (click)="onSave()"
      [disabled]="!isValidSelection()">
      Create Table
    </button>
  </mat-dialog-actions>
</div>